import { type ReactNode, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { DetailsContent, DetailsHeader, DetailsPage } from "components/app_layout/DetailsLayout";
import FilterDrawer from "components/filter_form/filter_drawer/FilterDrawer";
import Button from "components/ui/button/Button";
import SidebarOld from "components/ui/sidebar/SidebarOld";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import Badge from "lib/core_components/Badge";
import { useIsLaptopLgUp } from "lib/providers/Responsive";
import FilterPanelToggle from "./FilterPanelToggle";
import { useFilterPanelOpen } from "./useFilterPanelOpen";

const FILTER_PANEL_WIDTH = 360;

type SearchPageProps = {
  title: string;
  /**
   * Names this page's remembered filter-panel state, e.g. "spend". Per page, because the
   * answer differs by page: you might keep notice filters beside you and spend filters
   * out of the way.
   */
  storageKey: string;
  /** Sits right after the title, e.g. a beta icon or a saved/unsaved status pill. */
  titleBadge?: ReactNode;
  /** The filter form. Scrolls independently of the results. */
  filters: ReactNode;
  appliedFiltersCount: number;
  /** When given, a "Clear all" appears in the panel once at least one filter is applied. */
  onClearFilters?: () => void;
  /**
   * The headline for this search: a count, a count and a total, a count and how it is
   * sorted. The page composes the wording; the shell places it and swaps in a spinner.
   */
  summary?: ReactNode;
  isLoadingSummary?: boolean;
  /**
   * Sits at the far right of the results header. A page offering more than one view of the
   * same results puts its switch here; which control that is, is the page's choice.
   */
  viewMode?: ReactNode;
  /** The results themselves, below the results header. */
  children: ReactNode;
};

/**
 * The shell every search page shares: a sticky title, filters, a results header carrying
 * the count, and the results. It owns whether the filters are showing, and how — a column
 * beside the results on a wide screen, a drawer over them below laptop-lg, which is where
 * the rest of the app puts filters.
 */
export function SearchPage({
  title,
  storageKey,
  titleBadge,
  filters,
  appliedFiltersCount,
  onClearFilters,
  summary,
  isLoadingSummary = false,
  viewMode,
  children,
}: SearchPageProps) {
  const [filtersOpen, setFiltersOpen] = useFilterPanelOpen(storageKey);
  const inlineFilters = useIsLaptopLgUp();

  const panel = (
    <FilterPanel
      appliedFiltersCount={appliedFiltersCount}
      onClearFilters={onClearFilters}
      onCollapse={() => setFiltersOpen(false)}
    >
      {filters}
    </FilterPanel>
  );

  const resultsHeader = (
    <ResultsRow>
      <Flex alignItems="center" gap={8} minWidth={0}>
        <FilterPanelToggle
          open={inlineFilters && filtersOpen}
          onOpenChange={setFiltersOpen}
          appliedFiltersCount={appliedFiltersCount}
        />
        <Summary aria-live="polite">
          {isLoadingSummary ? (
            <SkeletonText
              role="status"
              aria-label="Loading results"
              width="min(360px, 100%)"
              customSpacing
              shimmer
            />
          ) : (
            summary
          )}
        </Summary>
      </Flex>
      {viewMode && (
        <Flex alignItems="center" gap={8} flexShrink={0}>
          {viewMode}
        </Flex>
      )}
    </ResultsRow>
  );

  return (
    <DetailsPage>
      <DetailsHeader>
        <Flex alignItems="center" gap={8} minWidth={0}>
          <Text h1 ellipsis>
            {title}
          </Text>
          {titleBadge}
        </Flex>
      </DetailsHeader>
      <PageContent>
        <PageMainRow>
          {inlineFilters ? (
            <SidebarOld open={filtersOpen} fillContainer width={FILTER_PANEL_WIDTH}>
              <InertWhenClosed open={filtersOpen}>{panel}</InertWhenClosed>
            </SidebarOld>
          ) : (
            <FilterDrawer open={filtersOpen} onClose={() => setFiltersOpen(false)}>
              {panel}
            </FilterDrawer>
          )}
          <MainArea>
            {resultsHeader}
            <ResultsScrollArea>{children}</ResultsScrollArea>
          </MainArea>
        </PageMainRow>
      </PageContent>
    </DetailsPage>
  );
}

/**
 * Chrome around the page's filter form: a heading with the applied count, an optional
 * Clear all, and a way to put it away.
 */
function FilterPanel({
  appliedFiltersCount,
  onClearFilters,
  onCollapse,
  children,
}: {
  appliedFiltersCount: number;
  onClearFilters?: () => void;
  onCollapse: () => void;
  children: ReactNode;
}) {
  return (
    <Panel aria-label="Filters panel">
      <PanelHeader>
        <Flex alignItems="center" gap={8}>
          <Heading as="h2" fontWeight="600" color="sysTextDefault">
            Filters
          </Heading>
          {appliedFiltersCount > 0 && (
            <FilterCountBadge size={18}>{appliedFiltersCount}</FilterCountBadge>
          )}
        </Flex>
        <Flex alignItems="center" gap={8}>
          {onClearFilters && appliedFiltersCount > 0 && (
            <Button buttonType="tertiary" onClick={onClearFilters}>
              Clear all
            </Button>
          )}
          <Button
            buttonType="secondary"
            icon="layoutLeft"
            aria-label="Collapse filters"
            onClick={onCollapse}
          />
        </Flex>
      </PanelHeader>
      <PanelBody>{children}</PanelBody>
    </Panel>
  );
}

/**
 * A collapsed inline panel is only clipped to zero width, so without this its form stays
 * in the tab order and in the accessibility tree: you could tab from "Show filters"
 * straight into filters you cannot see (WCAG 2.4.11). Kept mounted rather than unmounted
 * so a half-filled filter survives being put away.
 */
function InertWhenClosed({ open, children }: { open: boolean; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  // inert isn't in the repo's React 18 JSX types, so set it via ref rather than a cast.
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (open) {
      element.removeAttribute("inert");
    } else {
      element.setAttribute("inert", "");
    }
  }, [open]);

  return <PanelContents ref={ref}>{children}</PanelContents>;
}

/** Padded body for the results, below the results header. */
export const SearchResultsBody = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 24,
  padding: 24,
});

const PanelContents = styled.div({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
});

const Panel = styled.section({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
});

const PanelHeader = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16,
  padding: "16px 24px",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  flexShrink: 0,
}));

const PanelBody = styled.div({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
});

/** The count beside the heading, sized and spaced as notice search's is. */
const FilterCountBadge = styled(Badge)({
  marginLeft: 0,
});

const Heading = styled(Text)({
  fontSize: 14,
  lineHeight: "125%",
});

/**
 * Wraps the headline so the shell owns announcing it, rather than each page having to
 * remember. A search that silently changes its count is the thing a screen reader user
 * most needs told.
 */
const Summary = styled.div({
  display: "flex",
  alignItems: "center",
  minWidth: 0,
});

const ResultsRow = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16,
  padding: "16px 24px",
  minHeight: 64,
  boxSizing: "border-box",
  backgroundColor: theme.colors.sysBackgroundDefault,
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  flexShrink: 0,
}));

// `.pageScrollableContent` on DetailsPage is a flex column, so the sidebar can fill the
// available height (fillContainer) without measuring the sticky header.
const PageContent = styled(DetailsContent)({
  padding: 0,
  display: "flex",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
});

const PageMainRow = styled(Flex)({
  width: "100%",
  height: "100%",
  flex: 1,
  minHeight: 0,
});

const MainArea = styled.div({
  display: "flex",
  flexDirection: "column",
  flex: "1 1 0",
  minWidth: 0,
  minHeight: 0,
});

// Only the results scroll, so the results header stays in view above them.
const ResultsScrollArea = styled.div({
  flex: 1,
  // Lets this flex item shrink below its content height so the overflow can scroll.
  minHeight: 0,
  overflowY: "auto",
});
